/**
 * Blog listing progressive enhancement:
 * - AJAX topic filtering with native-link fallback
 * - Current-blog search with native article-search fallback
 * - Empty-topic recommendations
 */

const BLOG_SEARCH_MAX_PAGES = 250;

class BlogListing {
  #root;
  #filterController;
  #filterRequestController;
  #searchController;
  #searchRequestController;
  #articles;
  #defaultPostsHTML;

  constructor(root) {
    this.#root = root;
    this.#filterController = null;
    this.#filterRequestController = null;
    this.#searchController = null;
    this.#searchRequestController = null;
    this.#articles = null;
    this.#defaultPostsHTML = this.#postsContainer?.innerHTML || "";

    this.#initFilters();
    this.#initSearch();
    this.#initEmptyRecommendations();

    window.addEventListener("popstate", () => {
      this.#filterTo(window.location.href, false);
    });
  }

  get #postsContainer() {
    return this.#root.querySelector("[data-blog-posts-container]");
  }

  get #searchQuery() {
    return new URL(window.location.href).searchParams.get("blog_search")?.trim() || "";
  }

  #initFilters(topicDrawerOpen = false) {
    this.#filterController?.abort();
    this.#filterController = new AbortController();
    const { signal } = this.#filterController;
    const topicToggle = this.#root.querySelector("[data-blog-topic-toggle]");
    const topicPanel = this.#root.querySelector("[data-blog-topic-panel]");

    if (topicToggle && topicPanel) {
      topicToggle.hidden = false;
      topicToggle.setAttribute("aria-expanded", String(topicDrawerOpen));
      topicPanel.hidden = !topicDrawerOpen;

      topicToggle.addEventListener(
        "click",
        () => {
          const expanded = topicToggle.getAttribute("aria-expanded") === "true";
          topicToggle.setAttribute("aria-expanded", String(!expanded));
          topicPanel.hidden = expanded;
        },
        { signal }
      );
    }

    this.#root.querySelectorAll("[data-blog-filter-link]").forEach((link) => {
      link.addEventListener(
        "click",
        (event) => {
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
            return;
          }

          event.preventDefault();
          const url = new URL(link.href);
          const query = this.#searchQuery;

          if (query) {
            url.searchParams.set("blog_search", query);
          }

          this.#filterTo(url.href);
        },
        { signal }
      );
    });
  }

  async #filterTo(url, pushState = true) {
    this.#filterRequestController?.abort();
    this.#filterRequestController = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#filterRequestController.signal });

      if (!response.ok) {
        throw new Error(`Blog filter request failed: ${response.status}`);
      }

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, "text/html");
      const nextRoot = doc.querySelector("[data-blog-root]");

      if (!nextRoot) {
        window.location.href = url;
        return;
      }

      const nextTop = nextRoot.querySelector(".blog-page__top");
      const nextFeatured = nextRoot.querySelector(".blog-featured-post__container");
      const nextFilters = nextRoot.querySelector("[data-blog-filters]");
      const nextPosts = nextRoot.querySelector("[data-blog-posts-container]");
      const currentTop = this.#root.querySelector(".blog-page__top");
      const currentFeatured = this.#root.querySelector(".blog-featured-post__container");
      const currentFilters = this.#root.querySelector("[data-blog-filters]");
      const currentPosts = this.#postsContainer;
      const keepTopicDrawerOpen =
        currentFilters?.querySelector("[data-blog-topic-toggle]")?.getAttribute("aria-expanded") === "true";

      if (currentTop && nextTop) {
        currentTop.innerHTML = nextTop.innerHTML;
      }

      if (currentFeatured) {
        if (nextFeatured) {
          currentFeatured.replaceWith(nextFeatured);
        } else {
          currentFeatured.remove();
        }
      } else if (nextFeatured && currentFilters) {
        currentFilters.insertAdjacentElement("beforebegin", nextFeatured);
      } else if (nextFeatured && currentPosts) {
        currentPosts.parentElement.insertBefore(nextFeatured, currentPosts);
      }

      if (currentFilters) {
        if (nextFilters) {
          currentFilters.replaceWith(nextFilters);
        } else {
          currentFilters.remove();
        }
      } else if (nextFilters && currentPosts) {
        currentPosts.parentElement.insertBefore(nextFilters, currentPosts);
      }

      if (currentPosts && nextPosts) {
        currentPosts.innerHTML = nextPosts.innerHTML;
        this.#defaultPostsHTML = nextPosts.innerHTML;
      }

      this.#root.dataset.blogActiveTags = nextRoot.dataset.blogActiveTags || "[]";

      if (pushState) {
        window.history.pushState({}, "", url);
      }

      this.#initFilters(keepTopicDrawerOpen);
      this.#initSearch();
      this.#initEmptyRecommendations();

      const heading = this.#root.querySelector(".blog-page__header h1");

      if (heading) {
        document.title = `${heading.textContent.trim()} – ${window.Shopify?.shop || document.title}`;
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        window.location.href = url;
      }
    }
  }

  #initSearch() {
    this.#searchController?.abort();
    this.#searchController = new AbortController();
    const { signal } = this.#searchController;
    const search = this.#root.querySelector("[data-blog-search]");
    const form = search?.querySelector("[data-blog-search-form]");
    const input = search?.querySelector("[data-blog-search-input]");
    const clear = search?.querySelector("[data-blog-search-clear]");

    if (!search || !form || !input || !clear) {
      return;
    }

    const initialQuery = this.#searchQuery;
    input.value = initialQuery;
    clear.hidden = !initialQuery;

    form.addEventListener(
      "submit",
      (event) => {
        const query = input.value.trim();

        if (!query) {
          event.preventDefault();
          this.#clearSearch(true);
          return;
        }

        event.preventDefault();
        this.#setSearchURL(query);
        this.#runSearch(query);
      },
      { signal }
    );

    input.addEventListener(
      "input",
      () => {
        clear.hidden = !input.value;
      },
      { signal }
    );

    clear.addEventListener(
      "click",
      () => {
        input.value = "";
        clear.hidden = true;
        this.#clearSearch(true);
        input.focus();
      },
      { signal }
    );

    if (initialQuery) {
      this.#runSearch(initialQuery);
    }
  }

  #setSearchURL(query) {
    const url = new URL(window.location.href);
    url.searchParams.set("blog_search", query);
    window.history.pushState({}, "", url);
  }

  #clearSearch(pushState) {
    this.#searchRequestController?.abort();
    const url = new URL(window.location.href);
    url.searchParams.delete("blog_search");

    if (pushState) {
      window.history.pushState({}, "", url);
    }

    const postsContainer = this.#postsContainer;
    const status = this.#root.querySelector("[data-blog-search-status]");

    if (postsContainer) {
      postsContainer.innerHTML = this.#defaultPostsHTML;
    }

    if (status) {
      status.textContent = "";
    }

    this.#initFilters();
    this.#initEmptyRecommendations();
  }

  async #runSearch(query) {
    const normalizedQuery = this.#normalize(query);
    const status = this.#root.querySelector("[data-blog-search-status]");
    const postsContainer = this.#postsContainer;

    if (!normalizedQuery || !status || !postsContainer) {
      return;
    }

    this.#searchRequestController?.abort();
    this.#searchRequestController = new AbortController();
    status.textContent = status.dataset.loadingLabel || "Searching articles…";
    postsContainer.setAttribute("aria-busy", "true");

    try {
      const articles = await this.#loadArticles(this.#searchRequestController.signal);
      let activeTags = [];

      try {
        const parsed = JSON.parse(this.#root.dataset.blogActiveTags || "[]");
        activeTags = Array.isArray(parsed) && parsed.length ? [this.#normalize(parsed[0])] : [];
      } catch (error) {
        activeTags = [];
      }

      const terms = normalizedQuery.split(/\s+/).filter(Boolean);
      const results = articles.filter((article) => {
        const tags = Array.isArray(article.tags) ? article.tags : [];
        const normalizedTags = tags.map((tag) => this.#normalize(tag));
        const matchesTag = activeTags.length === 0 || normalizedTags.includes(activeTags[0]);
        const searchableText = this.#normalize(
          [article.title, article.summary_html, article.excerpt, article.body_html, article.content, tags.join(" ")].filter(Boolean).join(" ")
        );

        return matchesTag && terms.every((term) => searchableText.includes(term));
      });

      this.#renderSearchResults(results);
      status.textContent =
        results.length === 0
          ? status.dataset.emptyLabel || "No articles matched your search."
          : results.length === 1
            ? status.dataset.resultOneLabel || "1 article found"
            : (status.dataset.resultsLabel || "__COUNT__ articles found").replace("__COUNT__", results.length);
    } catch (error) {
      if (error.name !== "AbortError") {
        window.location.href = `/search?q=${encodeURIComponent(query)}&type=article`;
      }
    } finally {
      postsContainer.removeAttribute("aria-busy");
    }
  }

  async #loadArticles(signal) {
    if (this.#articles) {
      return this.#articles;
    }

    const blogHandle = this.#root.dataset.blogHandle;
    const articles = [];

    for (let page = 1; page <= BLOG_SEARCH_MAX_PAGES; page += 1) {
      const response = await fetch(`/blogs/${encodeURIComponent(blogHandle)}?view=search-json&page=${page}`, { signal });

      if (!response.ok) {
        throw new Error(`Blog search request failed: ${response.status}`);
      }

      const data = await response.json();
      const pageArticles = Array.isArray(data?.articles) ? data.articles : [];
      articles.push(...pageArticles);

      if (!data?.next_page) {
        break;
      }
    }

    this.#articles = articles;
    return articles;
  }

  #renderSearchResults(articles) {
    const postsContainer = this.#postsContainer;

    if (!postsContainer) {
      return;
    }

    const heading = document.createElement("div");
    heading.className = "blog-posts__heading";
    const title = document.createElement("h2");
    title.className = "h4";
    const query = this.#root.querySelector("[data-blog-search-input]")?.value.trim() || "";
    const headingTemplate =
      this.#root.querySelector("[data-blog-search-status]")?.dataset.headingLabel || "Search results for “__QUERY__”";
    title.textContent = headingTemplate.replace("__QUERY__", query);
    heading.appendChild(title);

    const grid = document.createElement("div");
    grid.className = "blog-posts blog-posts--listing";
    grid.dataset.blogPostsGrid = "";

    articles.forEach((article) => grid.appendChild(this.#createArticleCard(article)));
    postsContainer.replaceChildren(heading, grid);
  }

  #createArticleCard(article) {
    const card = document.createElement("article");
    card.className = "blog-post-card blog-post-card--listing";
    card.dataset.blogCard = "";

    const link = document.createElement("a");
    link.className = "blog-post-card__link";
    link.href = article.url;

    const figure = document.createElement("div");
    figure.className = "blog-post-card__figure blog-post-card__figure--listing";
    const imageSource = typeof article.image === "string" ? article.image : article.image?.src;

    if (imageSource) {
      const image = document.createElement("img");
      image.src = imageSource;
      image.alt = article.image?.alt || article.title || "";
      image.className = "blog-post-card__image w-full h-full object-cover zoom-image";
      image.loading = "lazy";
      figure.appendChild(image);
    }

    const info = document.createElement("div");
    info.className = "blog-post-card__info blog-post-card__info--listing";
    const body = document.createElement("div");
    body.className = "blog-post-card__body v-stack gap-3 sm:gap-4";
    const title = document.createElement("h3");
    title.className = "h3 blog-post-card__title line-clamp";
    title.style.setProperty("--line-clamp-count", "2");
    title.textContent = article.title || "";
    body.appendChild(title);

    const articleText = this.#plainText(article.summary_html || article.excerpt || article.body_html || article.content || "");

    if (this.#root.dataset.blogShowExcerpt === "true" && articleText) {
      const excerpt = document.createElement("p");
      excerpt.className = "blog-post-card__excerpt line-clamp";
      excerpt.style.setProperty("--line-clamp-count", "3");
      excerpt.textContent = articleText.length > 200 ? `${articleText.slice(0, 197).trim()}…` : articleText;
      body.appendChild(excerpt);
    }

    if (this.#root.dataset.blogShowDate === "true" || this.#root.dataset.blogShowReadTime === "true") {
      const meta = document.createElement("div");
      meta.className = "blog-post-card__meta h-stack gap-2 wrap";

      if (this.#root.dataset.blogShowDate === "true" && article.published_at) {
        const date = document.createElement("span");
        date.className = "blog-meta-pill";
        date.textContent = new Intl.DateTimeFormat(document.documentElement.lang || "en", {
          year: "numeric",
          month: "short",
          day: "numeric"
        }).format(new Date(article.published_at));
        meta.appendChild(date);
      }

      if (this.#root.dataset.blogShowReadTime === "true") {
        const readTime = document.createElement("span");
        readTime.className = "blog-meta-pill";
        const wordCount = articleText.split(/\s+/).filter(Boolean).length;
        readTime.textContent = `${Math.max(1, Math.ceil(wordCount / 200))} min read`;
        meta.appendChild(readTime);
      }

      body.appendChild(meta);
    }

    info.appendChild(body);
    link.append(figure, info);
    card.appendChild(link);

    const tags = Array.isArray(article.tags) ? article.tags : [];

    if (this.#root.dataset.blogShowCategory === "true" && tags[0]) {
      const tag = document.createElement("a");
      tag.className = "blog-post-card__tag blog-topic-pill";
      tag.href = `/blogs/${encodeURIComponent(this.#root.dataset.blogHandle)}/tagged/${this.#handleize(tags[0])}`;
      tag.textContent = tags[0];
      card.appendChild(tag);
    }

    return card;
  }

  #plainText(html) {
    const doc = new DOMParser().parseFromString(String(html), "text/html");
    return doc.body.textContent.replace(/\s+/g, " ").trim();
  }

  #normalize(value) {
    return this.#plainText(value).toLocaleLowerCase();
  }

  #handleize(value) {
    return this.#normalize(value)
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
  }

  async #initEmptyRecommendations() {
    const emptyState = this.#root.querySelector("[data-blog-empty-tag]");
    const recommendations = emptyState?.querySelector("[data-blog-empty-recommendations]");
    const grid = recommendations?.querySelector("[data-blog-posts-grid]");
    const blogHandle = this.#root.dataset.blogHandle;

    if (!emptyState || !recommendations || !grid || !blogHandle) {
      return;
    }

    try {
      const response = await fetch(`/blogs/${encodeURIComponent(blogHandle)}?view=search-json&page=1`);

      if (!response.ok) {
        return;
      }

      const data = await response.json();

      if (!data?.articles?.length) {
        return;
      }

      grid.replaceChildren(...data.articles.slice(0, 3).map((article) => this.#createArticleCard(article)));
      recommendations.hidden = false;
    } catch (error) {
      recommendations.hidden = true;
    }
  }
}

const initializeBlogListings = () => {
  document.querySelectorAll("[data-blog-root]").forEach((root) => {
    new BlogListing(root);
  });
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initializeBlogListings, { once: true });
} else {
  initializeBlogListings();
}
